import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { MultiValue, SingleValue } from "react-select";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import Input from "../../../../components/input";
import Button from "../../../../components/button";
import {
  SummarySupportResource,
  WeeklySummaryResponse,
} from "../../../../redux/features/weekly-summary-slice";
import {
  CommonStatus,
  getSupportResourceState,
  SupportResourceResponse,
  supportResourcesStateAction,
} from "../../../../redux/features/other-slice";
import { OptionValue } from "../../../../types/component";
import { isSelectSingleValue } from "../../../../core/utils";
import { AppDispatch } from "../../../../redux/store";
import { getUserState } from "../../../../redux/features/auth-slice";
import { InfoOutlined } from "../../../../components/icons/info-outlined";
import StyledSelect from "../../../../components/styled-select";
import SummaryPropertyDiscussDialog from "../summary-property-discuss-dialog";
import { TrashOutlined } from "../../../../components/icons/trash-outlined";
import { CheckOutlined } from "../../../../components/icons/check-outlined";
import { EditOutlined } from "../../../../components/icons/edit-outlined";

interface Props {
  summary: WeeklySummaryResponse | null;
  resources: SummarySupportResource[];
  setResources: (values: SummarySupportResource[]) => void;
  onRefresh: () => void;
}

export default function SupportResourceForm(props: Props) {
  const { summary, resources, setResources, onRefresh } = props;

  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector(getUserState).data;
  const resourcesState = useSelector(getSupportResourceState);

  const [selectedResource, setSelectedResource] =
    useState<SummarySupportResource | null>(null);

  const [editId, setEditId] = useState<string | null>(null); 
  const [editValues, setEditValues] = useState<SummarySupportResource | null>(null); 

  const getAvailableOptions = () => {
    const selectedTypes = resources.map((resource) => resource.type.id);
    const availableResources = resourcesState.data.filter(
      (resource:SupportResourceResponse) => !selectedTypes.includes(resource.id)
    );
    return [
      {
        id: "null",
        title: "Nil",
        description: "Nothing",
        category: {
          id: "",
          title: "",
          status: CommonStatus.Inactive,
        },
      },
      ...availableResources,
    ];
  };

  const fetchResources = useCallback(async () => {
    if (user) {
      await dispatch(
        supportResourcesStateAction({
          categoryId: user.category.id,
        })
      );
    }
  }, [dispatch, user]);

  useEffect(() => {
    fetchResources();
  }, [fetchResources]);

  return (
    <>
      <div className="flex flex-col space-y-4">
        {resources.map((sr: SummarySupportResource) => {
          const isEditing = editId === sr.type.id;
          return (
          <div
            key={sr.type.id}
            className="flex flex-col items-center space-y-4 p-4 border border-divider bg-background-default rounded-lg"
          >
            <div className="w-full flex flex-col space-y-4">
              <div>
                <label className="w-1/4 text-xs text-text-title">Type</label>
                {isEditing ? (
                  <div>
                    <StyledSelect
                      loading={resourcesState.loading}
                      menuPosition="fixed"
                      options={getAvailableOptions().map(
                        (resource: SupportResourceResponse) => ({
                          value: resource.id,
                          label: resource.title,
                        })
                      )}
                      isClearable={false}
                      value={
                        editValues &&
                        editValues.type.id &&
                        editValues.type.title
                          ? {
                              value: editValues.type.id,
                              label: editValues.type.title,
                            }
                          : undefined
                      }
                      onChange={(
                        value:
                          | SingleValue<OptionValue>
                          | MultiValue<OptionValue>
                      ) => {
                        if (value && isSelectSingleValue(value)) {
                          setEditValues({
                            ...editValues!,
                            type: {
                              id: value.value as string,
                              title: value.label as string,
                            },
                            explain: value.value === "null" ? "--" : "",
                          });
                        }
                      }}
                      exClassName="w-full"
                    />
                    {editValues?.type.id && (
                      <label className="flex items-center text-text-title text-xs mt-1">
                        <InfoOutlined className="h-4 w-4 mr-1" />
                        {editValues.type.id === "null"
                          ? "Nothing"
                          : resourcesState.data.filter(
                              (e) => e.id === editValues.type.id
                            ).length > 0
                          ? resourcesState.data.filter(
                              (e) => e.id === editValues.type.id
                            )[0].description
                          : ""}
                      </label>
                    )}
                  </div>
                ) : (
                  <Input
                    type="text"
                    inputType="textarea"
                    rows={3}
                    value={sr.type.title}
                    readonly
                  />
                )}
              </div>
              <div>
                <label className="text-xs text-text-title">Explain</label>
                <Input
                  type="text"
                  inputType="textarea"
                  rows={3}
                  value={isEditing ? editValues?.explain : sr.explain}
                  readonly={
                    isEditing
                      ? editValues?.type.id === "null"
                      : sr.type.id === "null"
                  }
                  onChange={(event) => {
                    if (isEditing) {
                      setEditValues({
                        ...editValues!,
                        explain: event.target.value,
                      });
                    }
                  }}
                  errorText={
                    isEditing && editValues?.explain === ""
                      ? "This field is required"
                      : undefined
                  }
                />
              </div>
            </div>
            <div className="w-full flex items-center justify-end space-x-2">
              {summary &&
                summary.supportResources.findIndex(
                  (e) => e.type.id === sr.type.id && e.explain === sr.explain
                ) !== -1 &&
                sr.type.id !== "null" && (
                  <Button
                    variant="outlined"
                    label={`Discusses (${sr.discuss.length})`}
                    onClick={() => setSelectedResource(sr)}
                    exClassName="w-fit border-divider"
                  />
                )}
              <div className="flex flex-1 items-center justify-end space-x-2">
                <div
                  className="p-2 rounded cursor-pointer hover:bg-gray-200"
                  onClick={() => {
                    if (isEditing) {
                      if (
                        editValues?.type.id &&
                        editValues.type.title &&
                        editValues.explain
                      ) {
                        const editIndex = resources.findIndex(
                          (e) =>
                            e.type.id === sr.type.id &&
                            e.explain === sr.explain
                        );
                        if (editIndex !== -1) {
                          const tmp = [...resources];
                          tmp[editIndex] = {
                            ...tmp[editIndex],
                            type: editValues.type,
                            explain: editValues.explain,
                          };
                          setResources(tmp);
                          setEditValues(null);
                          setEditId(null);
                        }
                      }
                    } else {
                      // Enter edit mode
                      setEditId(sr.type.id);
                      setEditValues(sr);
                    }
                  }}
                >
                  {isEditing ? (
                    <CheckOutlined className="h-4 w-4 text-success-main" />
                  ) : (
                    <EditOutlined className="h-4 w-4" />
                  )}
                </div>
                <div
                  className="p-2 rounded cursor-pointer hover:bg-gray-200"
                  onClick={() => {
                    const removedIndex = resources.findIndex(
                      (e) =>
                        e.type.id === sr.type.id && e.explain === sr.explain
                    );
                    if (removedIndex !== -1) {
                      const tmp = resources.filter(
                        (_, index) => index !== removedIndex
                      );
                      setResources(tmp);
                    }
                  }}
                >
                  <TrashOutlined className="h-4 w-4 text-error-main" />
                </div>
              </div>
            </div>
          </div>
          );
        })}
        {resources.findIndex((e) => e.type.id === "null") === -1 && (
          <Formik
            initialValues={{ id: "", title: "", explain: "" }}
            validationSchema={Yup.object().shape({
              id: Yup.string().required("This field is required"),
              title: Yup.string().required("This field is required"),
              explain: Yup.string().required("This field is required"),
            })}
            onSubmit={(values, { setSubmitting, resetForm }) => {
              setSubmitting(false);
              setResources([
                ...resources,
                {
                  type: {
                    id: values.id,
                    title: values.title,
                  },
                  explain: values.explain,
                  discuss: [],
                },
              ]);
              resetForm();
            }}
          >
            {({ values, setValues, errors, handleChange, handleSubmit }) => (
              <Form
                noValidate
                autoComplete="off"
                onSubmit={handleSubmit}
                className="flex flex-col items-center space-y-4 border border-divider rounded-lg p-4"
              >
                <div className="w-full flex flex-col space-y-4">
                  <div>
                    <label className="text-xs text-text-title">Type</label>
                    <div className="flex flex-col">
                      <StyledSelect
                        loading={resourcesState.loading}
                        menuPosition="fixed"
                        options={getAvailableOptions().map(
                          (resource: SupportResourceResponse) => ({
                            value: resource.id,
                            label: resource.title,
                          })
                        )}
                        isClearable={false}
                        value={
                          values.id !== "" && values.title !== ""
                            ? { value: values.id, label: values.title }
                            : { value: "", label: "Select..." }
                        }
                        onChange={(
                          value:
                            | SingleValue<OptionValue>
                            | MultiValue<OptionValue>
                        ) => {
                          if (value && isSelectSingleValue(value)) {
                            setValues({
                              ...values,
                              id: value.value as string,
                              title: value.label as string,
                              explain:
                                (value.value as string) === "null" ? "--" : "",
                            });
                          }
                        }}
                        exClassName="w-full"
                      />
                      {values.id && (
                        <label className="flex items-center text-text-title text-xs mt-1">
                          <InfoOutlined className="h-4 w-4 mr-1" />
                          {values.id === "null"
                            ? "Nothing"
                            : resourcesState.data.filter(
                                (e) => e.id === values.id
                              ).length > 0
                            ? resourcesState.data.filter(
                                (e) => e.id === values.id
                              )[0].description
                              : ""}
                        </label>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-text-title">Explain</label>
                    <Input
                      type="text"
                      inputType="textarea"
                      rows={3}
                      id="explain"
                      value={values.explain}
                      onChange={handleChange}
                      readonly={values.id === "null"}
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  label="Add"
                  exClassName="w-fit self-end"
                  disabled={
                    Array.isArray(errors) ||
                    Object.values(errors).toString() != ""
                  }
                />
              </Form>
            )}
          </Formik>
        )}
      </div>
      {summary && (
        <SummaryPropertyDiscussDialog
          summaryId={summary.id}
          type="resource"
          property={selectedResource}
          setProperty={setSelectedResource}
          onSubmitted={onRefresh}
        />
      )}
    </>
  );
}
